import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Row,
  Col,
  Button,
  Input,
  Spin,
  message,
  Card,
  Table,
  Tag,
  Tooltip,
  Modal,
  DatePicker,
  Select,
} from "antd";
import {
  CaretDownOutlined,
  CaretUpOutlined,
  QuestionCircleFilled,
  UserOutlined,
  ProfileOutlined,
  RightOutlined,
  QrcodeOutlined,
} from "@ant-design/icons";
import {
  apiGetMember,
  apiGetMeWallet,
  apiGetMeWalletTransaction,
  apiPatchMember,
  apiResetPsd,
  apiGetOrder,
  apiGetOrderById,
  apiGetOrderDetail,
  apiGetOtp,
  apiConfirmOTP,
  apiVerifyEmail,
  apiCreateSwitchTicket,
} from "api/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { convertTimeStamp } from "utility/functions/convertTimestamp";
import { errorHandler } from "utility/functions/errorHandler";

import { Columns, Columns_mobile } from "./columns";
import { orderStatus } from "./options";
import { paymentMethodMap } from "utility/options/paymentMethod";
import { useTranslation } from "react-i18next";

import DetailModal from "./components/DetailModal";
import ExchangeTicketModal, {
  checkAvailableTickets,
} from "./components/ExchangeTicketModal";
import { apiErrorHandler } from "api";
import PhoneInput from "components/PhoneInput";

const getOrderStatusLabel = (status) =>
  orderStatus.find((item) => item.value === status)?.label || "-";

const DEFAULT_HISTORY_TAB = 4;
const VALID_HISTORY_TABS = new Set([1, 2, 4, 8, 16, 32]);

const getWalletTransactionLabel = (type, amount) => {
  const numericType = Number(type);
  const labels = {
    1: "購買點數",
    2: "購票折抵",
    3: "退款回饋",
    4: "點數凍結",
    5: "點數解凍",
    7: "點數已到期",
    TopUp: "購買點數",
    Spend: "購票折抵",
    Refund: "退款回饋",
    Freeze: "點數凍結",
    Unfreeze: "點數解凍",
    Expired: "點數已到期",
  };

  if (numericType === 6 || type === "Adjust") {
    return Number(amount) < 0 ? "點數更正" : "客服贈點";
  }

  return labels[type] || type || "-";
};

const hasOrderDetails = (type) =>
  [1, 2, 3].includes(Number(type)) ||
  ["TopUp", "Spend", "Refund"].includes(type);

const DetailRow = ({ label, children, strong }) => (
  <div className="grid grid-cols-[120px_1fr] gap-2 py-1 mobile:grid-cols-1 mobile:gap-0">
    <p className="m-0 text-SKF-G-002">{label}</p>
    <div
      className={`m-0 text-black ${
        strong ? "font-bold text-SKF-Blue-001" : ""
      }`}
    >
      {children === null || children === undefined || children === ""
        ? "-"
        : children}
    </div>
  </div>
);

const OrderInfoItem = ({ label, value, strong }) => (
  <div className="min-w-0">
    <p className="m-0 text-sm leading-snug text-SKF-G-002 mobile:text-[13px]">
      {label}
    </p>
    <p
      className={`m-0 mt-1 break-words leading-snug mobile:text-[15px] ${
        strong ? "font-bold text-SKF-Blue-001" : "text-SKF-G-003"
      }`}
    >
      {value === null || value === undefined || value === "" ? "-" : value}
    </p>
  </div>
);

const getStatusTone = (status) => {
  if (status === 4) return "green";
  if (status === 64 || status === 32) return "red";
  return "blue";
};

const getOrderDate = (order) =>
  order?.startDateTime || order?.creationDate || order?.purchaseDate;

const getWalletDiscountAmount = (order) => {
  if (
    order?.walletDiscountAmount === null ||
    order?.walletDiscountAmount === undefined ||
    order?.walletDiscountAmount === ""
  )
    return null;
  return Number(order.walletDiscountAmount);
};

const formatWalletDiscountAmount = (order) => {
  const amount = getWalletDiscountAmount(order);
  if (amount === null || Number.isNaN(amount)) return "-";
  return Math.abs(amount).toLocaleString();
};

const formatOrderAmount = (amount) => {
  if (amount === null || amount === undefined || amount === "") return "-";
  const parsedAmount = Number(amount);
  if (Number.isNaN(parsedAmount)) return amount;
  return parsedAmount.toLocaleString();
};

const isUpcomingOrder = (order) => {
  const date = getOrderDate(order);
  if (!date) return true;
  return new Date(date).getTime() >= Date.now();
};

const Member = () => {
  const [form] = Form.useForm();

  const { t } = useTranslation();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isLogIn =
    sessionStorage._skyFilm_t && sessionStorage._skyFilm_t.length > 0;

  const [isLoading, setIsLoading] = useState(false);
  const [isValidEmailLoading, setIsValidEmailLoading] = useState(false);
  const [isCardLoading, setIsCardLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [upcomingActivities, setUpcomingActivities] = useState([]);
  const [record, setRecord] = useState({});
  const [otpKey, setOtpKey] = useState("");
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [activeSection, setActiveSection] = useState(() =>
    searchParams.get("section") === "history" ? "history" : "info"
  );
  const [historyTab, setHistoryTab] = useState(() => {
    const requestedTab = Number(searchParams.get("status"));
    return VALID_HISTORY_TABS.has(requestedTab)
      ? requestedTab
      : DEFAULT_HISTORY_TAB;
  });
  const [isAccountInfoOpen, setIsAccountInfoOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1023);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const [transactions, setTransactions] = useState([]);
  const [isTransactionsLoading, setIsTransactionsLoading] = useState(false);
  const [pointQuery, setPointQuery] = useState({
    limit: 10,
    offset: 0,
    type: undefined,
    startDateTime: undefined,
    endDateTime: undefined,
    sourceId: undefined,
  });
  const [pointTotal, setPointTotal] = useState(0);
  const [searchDates, setSearchDates] = useState([]);
  const [searchType, setSearchType] = useState(undefined);
  const [searchOrderId, setSearchOrderId] = useState("");

  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [orderRecord, setOrderRecord] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFormChange, setIsFormChange] = useState(false);
  const [isEmailCodeShow, setIsEmailCodeShow] = useState(false);
  const [formData, setFormData] = useState({});

  const [isExchangeTicketModalOpen, setIsExchangeTicketModalOpen] =
    useState(false);
  const [exchangeTicketRecord, setExchangeTicketRecord] = useState(null);
  const [skyPointBalance, setSkyPointBalance] = useState(0);

  const pendingTargetOrderIdRef = useRef(null);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await apiGetMember();
      const { content } = res.data;
      form.setFieldsValue(content);
      setFormData(content);
    } catch (err) {
      console.log("err", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWallet = async () => {
    try {
      const res = await apiGetMeWallet();
      const { totalBalance, totalFrozenBalance } = res.data.content || {};
      setSkyPointBalance((totalBalance || 0) - (totalFrozenBalance || 0));
    } catch (err) {
      console.log("err", err);
    }
  };

  useEffect(() => {
    if (isLogIn) {
      fetchData();
      fetchWallet();
    } else navigate("/login");
  }, [activeSection, isLogIn]);

  const fetchOrder = async (status) => {
    setIsCardLoading(true);
    try {
      const res = await apiGetOrder({ status });
      const { content } = res.data;
      const targetId = pendingTargetOrderIdRef.current;
      if (targetId) pendingTargetOrderIdRef.current = null;
      const mappedOrders = (content || []).map((item, index) => ({
        ...item,
        isActiveCollapse: targetId ? item.id === targetId : index < 2,
        details: (item.details || []).map((dt) => ({
          ...dt,
          key: dt.id,
          isReturnBtnLoading: false,
          isExchangeBtnLoading: false,
        })),
      }));
      setOrders(mappedOrders);
    } catch (err) {
      console.log("error", err);
    } finally {
      setIsCardLoading(false);
    }
  };

  const handleNavigateToOrder = async (sourceId) => {
    if (!sourceId) return;
    try {
      const res = await apiGetOrderById(sourceId);
      const order = res.data?.content;
      if (!order) return;
      setActiveSection("history");
      if (order.status === historyTab) {
        setOrders((prev) =>
          prev.map((o) => ({ ...o, isActiveCollapse: o.id === sourceId }))
        );
      } else {
        pendingTargetOrderIdRef.current = sourceId;
        setHistoryTab(order.status);
      }
    } catch (err) {
      console.log("err", err);
    }
  };

  const fetchUpcomingSummary = async () => {
    try {
      const res = await apiGetOrder({ ActivityTimeFilter: 1, status: 4 });
      const { content } = res.data;
      const mapped = (content || []).map((item) => ({
        ...item,
        details: (item.details || []).map((dt) => ({
          ...dt,
          key: dt.id,
        })),
      }));
      setUpcomingActivities(mapped);
    } catch (err) {
      console.log("err", err);
    }
  };

  useEffect(() => {
    if (isLogIn) {
      fetchOrder(historyTab);
    }
  }, [historyTab, isLogIn]);

  useEffect(() => {
    if (isLogIn) {
      fetchUpcomingSummary();
    }
  }, [isLogIn]);

  const fetchTransactions = async () => {
    setIsTransactionsLoading(true);
    try {
      const { limit, offset, type, startDateTime, endDateTime, sourceId } =
        pointQuery;
      const params = { limit, offset };
      if (type !== undefined && type !== null && type !== "")
        params.Type = type;
      if (startDateTime) params.StartDateTime = startDateTime;
      if (endDateTime) params.EndDateTime = endDateTime;
      if (sourceId) params.sourceId = sourceId;

      const res = await apiGetMeWalletTransaction(params);
      setTransactions(res.data?.content || []);
      setPointTotal(
        res.data?.totalElements ||
          res.data?.total ||
          res.data?.content?.length ||
          0
      );
    } catch (err) {
      console.log("err", err);
    } finally {
      setIsTransactionsLoading(false);
    }
  };

  useEffect(() => {
    if (isLogIn && activeSection === "points") {
      fetchTransactions();
    }
  }, [pointQuery, activeSection, isLogIn]);

  const fetchOrderAndShowModal = async (orderId) => {
    try {
      const res = await apiGetOrderById(orderId);
      setOrderRecord(res.data?.content);
      setIsOrderModalOpen(true);
    } catch (err) {
      console.log("err", err);
    }
  };

  const fetchDetail = async (detailRecord) => {
    try {
      const res = await apiGetOrderDetail(detailRecord.id);
      const { content } = res.data;
      setRecord(content);
      setIsModalOpen(true);
    } catch (err) {
      console.log("err", err);
    }
  };

  const updateInfo = async (data) => {
    setIsEditLoading(true);
    try {
      await apiPatchMember(data);
      message.success("儲存成功");
      setIsFormChange(false);
      fetchData();
    } catch (error) {
      errorHandler(error, t);
    } finally {
      setIsEditLoading(false);
    }
  };

  const onFinish = (values) => {
    updateInfo(values);
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const logOut = () => {
    sessionStorage.removeItem("_skyFilm_t");
    localStorage.removeItem("carts");
    window.dispatchEvent(new Event("logout"));
    setTimeout(() => {
      navigate("/home");
      navigate(0);
    }, 500);
  };

  const onValuesChange = () => {
    setIsFormChange(true);
  };

  const handleVerify = () => {
    sendOtp();
  };

  const sendOtp = async () => {
    setIsValidEmailLoading(true);
    try {
      const res = await apiGetOtp({ email: form.getFieldValue("email") });
      setOtpKey(res.data.content);
      message.success(`已發送驗證碼至${form.getFieldValue("email")}`);
      setIsEmailCodeShow(true);
    } catch (error) {
      errorHandler(error, t);
    } finally {
      setIsValidEmailLoading(false);
    }
  };

  const handleEmailOTP = async (otp) => {
    setIsOtpLoading(true);
    try {
      const res = await apiConfirmOTP({
        otp,
        key: otpKey,
      });
      setTimeout(() => {
        verifyEmail(res.data.content);
      }, 5000);
    } catch (error) {
      errorHandler(error, t);
    }
  };

  const verifyEmail = async (token) => {
    try {
      await apiVerifyEmail(token);
      setIsEmailCodeShow(false);
      message.success("Email認證成功，請使用新信箱重新登入", 4);
      sessionStorage.removeItem("_skyFilm_t");
      setTimeout(() => {
        navigate("/login");
      }, 4500);
    } catch (error) {
      errorHandler(error, t);
    } finally {
      setIsOtpLoading(false);
      fetchData();
    }
  };

  const resetPsd = async () => {
    try {
      const res = await apiResetPsd();
      const { content } = res.data;
      message.success("已經發送驗證碼至您的信箱");
      sessionStorage.k = content;
      setTimeout(() => {
        navigate("/confirm-otp");
      }, 1500);
    } catch (err) {
      console.log("err", err);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const toggleOrder = (orderId) => {
    setOrders((prev) =>
      prev.map((order) =>
        order.id === orderId
          ? { ...order, isActiveCollapse: !order.isActiveCollapse }
          : order
      )
    );
  };

  const handleReturnTicket = async (detailRecord) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) => ({
        ...order,
        details: order.details.map((detail) =>
          detail.id === detailRecord.id
            ? { ...detail, isReturnBtnLoading: true }
            : detail
        ),
      }))
    );

    try {
      // TODO: Wire to refund API when refund request payload/status handling is confirmed.
      setTimeout(() => {
        alert("申請退票！");
        setOrders((prevOrders) =>
          prevOrders.map((order) => ({
            ...order,
            details: order.details.map((detail) =>
              detail.id === detailRecord.id
                ? { ...detail, isReturnBtnLoading: false }
                : detail
            ),
          }))
        );
      }, 3000);
    } catch (error) {
      apiErrorHandler(error.re, t);
      setOrders((prevOrders) =>
        prevOrders.map((order) => ({
          ...order,
          details: order.details.map((detail) =>
            detail.id === detailRecord.id
              ? { ...detail, isReturnBtnLoading: false }
              : detail
          ),
        }))
      );
    }
  };

  const handleExchangeTicket = async (detailRecord) => {
    const hasAvailableTickets = await checkAvailableTickets(detailRecord.id);

    if (hasAvailableTickets) {
      setExchangeTicketRecord(detailRecord);
      setIsExchangeTicketModalOpen(true);
    }
  };

  const handleExchangeTicketOk = async (data) => {
    const { selectedSession, originalRecord } = data;
    try {
      await apiCreateSwitchTicket(
        { targetId: selectedSession },
        originalRecord?.id
      );
      message.success(
        `正在為票券 ${originalRecord.title} 申請換到場次 ${selectedSession}`
      );
    } catch (error) {
      message.error("換票失敗，請稍後再試");
    }

    setIsExchangeTicketModalOpen(false);
    setExchangeTicketRecord(null);
  };

  const handleExchangeTicketCancel = () => {
    setIsExchangeTicketModalOpen(false);
    setExchangeTicketRecord(null);
  };

  const navItems = [
    { key: "info", title: "基本資料", subtitle: "Information" },
    { key: "points", title: "我的點數", subtitle: "My Point" },
    { key: "history", title: "消費紀錄", subtitle: "Purchase history" },
  ];

  const visibleOrders = orders;

  const upcomingOrders = upcomingActivities.slice(0, 1);

  const renderDescription = (desc, orderId) => {
    // Attempt to extract order ID from description if orderId is not provided
    const extractedId =
      orderId ||
      (desc?.match(/#([a-zA-Z0-9]+)/)
        ? desc.match(/#([a-zA-Z0-9]+)/)[1]
        : null);
    if (extractedId) {
      return (
        <Button
          type="link"
          className="p-0 text-black font-bold h-auto whitespace-normal text-left"
          onClick={() => fetchOrderAndShowModal(extractedId)}
        >
          {desc}
        </Button>
      );
    }
    return desc || "-";
  };

  const ticketColumns = isMobile
    ? Columns_mobile({
        fetchDetail,
        order: {},
        handleReturnTicket,
        handleExchangeTicket,
      })
    : Columns({
        fetchDetail,
        handleReturnTicket,
        order: {},
        handleExchangeTicket,
      });

  const renderSidebar = () => (
    <aside className="w-[160px] shrink-0 border-0 border-r border-solid border-[#e8e8e8] bg-[#fafafa] mobile:w-full mobile:border-r-0 mobile:border-b">
      <div className="sticky top-[64px] mobile:static">
        {navItems.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setActiveSection(item.key)}
            className={`relative block w-full border-0 bg-transparent px-5 py-4 text-left cursor-pointer transition mobile:inline-block mobile:w-1/3 mobile:px-3 mobile:py-4 ${
              activeSection === item.key ? "bg-white" : "bg-transparent"
            }`}
          >
            <span
              className={`absolute left-0 top-0 h-full w-1 bg-black ${
                activeSection === item.key ? "block" : "hidden"
              } mobile:h-1 mobile:w-full`}
            />
            <span className="block text-base font-bold text-black mobile:text-sm">
              {item.title}
            </span>
            <span className="block text-sm text-SKF-G-002 mobile:text-xs">
              {item.subtitle}
            </span>
          </button>
        ))}
      </div>
    </aside>
  );

  const renderAccountFormFields = () => (
    <Form
      name="member"
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      autoComplete="off"
      form={form}
      layout="vertical"
      initialValues={formData}
      onValuesChange={onValuesChange}
      className="px-6 py-5 mobile:px-4"
    >
      <Row gutter={16}>
        <Col sm={{ span: 24 }} xs={{ span: 24 }}>
          <Form.Item label="Name 姓名" name="name">
            <Input placeholder="名字" />
          </Form.Item>
        </Col>
        <Col span={24} className="mb-5">
          <Form.Item
            label="Email 電子郵件"
            name="email"
            tooltip="若修改，登入將使用更改後的Email"
            className="mb-1"
          >
            <Input placeholder="Email" />
          </Form.Item>
          {!formData.emailConfirmed && (
            <Button
              type="primary"
              className="mt-1 rounded-xl float-left"
              loading={isValidEmailLoading}
              onClick={handleVerify}
            >
              驗證Email
            </Button>
          )}
        </Col>
        {isEmailCodeShow && (
          <Card className="mb-5 w-full rounded-2xl bg-SKF-G-000 text-left shadow-sm desktop:[&_.ant-otp]:w-1/4 tablet:[&_.ant-otp]:w-1/2 mobile:[&_.ant-otp]:w-full tablet:[&_.ant-otp-input]:py-3 mobile:[&_.ant-otp-input]:py-3">
            <p className="mb-3 text-base font-semibold">請輸入驗證碼：</p>
            <Card
              loading={isOtpLoading}
              className="border-none bg-SKF-G-000 [&_.ant-card-body]:p-0"
            >
              <Input.OTP
                variant="filled"
                type="number"
                onChange={handleEmailOTP}
              />
            </Card>
          </Card>
        )}
        <Col span={24}>
          <Form.Item label="Mobile Phone 手機號碼" name="phone" className="m-0">
            <PhoneInput size="large" placeholder="電話" />
          </Form.Item>
        </Col>
        <Col span={24} className="mt-6 flex items-center justify-between">
          <Button
            type="link"
            className="p-0 text-SKF-Blue-001 mobile:mb-4"
            onClick={resetPsd}
          >
            重新設定密碼
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            className="rounded-xl bg-black text-white !p-0"
            loading={isEditLoading}
            disabled={!isFormChange}
          >
            儲存
          </Button>
        </Col>
      </Row>
    </Form>
  );

  const renderProfileCard = () => (
    <Card className="rounded-2xl shadow-sm [&_.ant-card-body]:p-0">
      <div className="flex items-center gap-4 border-0 border-b border-solid border-[#ededed] p-6 mobile:p-4">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[#f1f1f1] text-SKF-G-002 mobile:h-12 mobile:w-12">
          <UserOutlined className="text-3xl mobile:text-xl" />
        </div>
        <div className="min-w-0 flex-1 text-left">
          <h3 className="m-0 text-2xl font-bold mobile:text-lg">
            Hello, {formData?.name || "Skyline Film"}
          </h3>
          <p className="m-0 mt-1 truncate text-sm text-SKF-G-002">
            {formData?.email || "-"}
          </p>
        </div>
        <Button className="rounded-xl" onClick={logOut}>
          登出
        </Button>
      </div>
      <button
        type="button"
        onClick={() => setIsAccountInfoOpen((value) => !value)}
        className="flex w-full cursor-pointer items-center justify-between border-0 border-b border-solid border-[#ededed] bg-white px-6 py-4 mobile:px-4"
      >
        <div className="text-left">
          <p className="m-0 font-bold text-black">帳戶資訊</p>
          <p className="m-0 text-sm text-SKF-G-002">Account Info</p>
        </div>
        {isAccountInfoOpen ? (
          <CaretUpOutlined className="rounded-xl bg-[#f3f3f3] p-2 text-SKF-G-003" />
        ) : (
          <CaretDownOutlined className="rounded-xl bg-[#f3f3f3] p-2 text-SKF-G-003" />
        )}
      </button>
      {isAccountInfoOpen && renderAccountFormFields()}
    </Card>
  );

  const renderPointBanner = () => (
    <div className="flex items-center justify-between rounded-2xl bg-gradient-to-b from-[#bfe3f2] to-[#e1f0f7] px-7 py-6 text-left mobile:px-5">
      <div>
        <p
          className="m-0 text-xs tracking-[0.2em] text-[#5c859f]"
          style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600 }}
        >
          SKY POINT
        </p>
        <p
          className="m-0 mt-2 text-4xl leading-none text-[#1a3d5d] mobile:text-4xl"
          style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
        >
          {skyPointBalance.toLocaleString()}
          <span
            className="ml-2 text-xl text-[#6da3b9]"
            style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 500 }}
          >
            pts
          </span>
        </p>
        <p
          className="m-0 mt-2 text-xs text-[#6da3b9]"
          style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 500 }}
        >
          1 pt = NT$1
        </p>
      </div>
      <div className="flex flex-col gap-2 text-right items-end">
        <Button
          type="link"
          className="h-auto p-0 flex items-center justify-end text-[#0066cc] font-semibold text-sm hover:opacity-80 transition-opacity"
          onClick={() => setActiveSection("points")}
        >
          查看點數明細 <RightOutlined className="text-[10px]" />
        </Button>
        <Button
          type="link"
          className="h-auto p-0  flex items-center justify-end text-[#0066cc] font-semibold text-sm hover:opacity-80 transition-opacity"
        >
          獲取點數 <RightOutlined className="text-[10px]" />
        </Button>
      </div>
    </div>
  );

  const renderInfo = () => (
    <div className="mx-auto max-w-[1020px] space-y-6">
      {renderProfileCard()}
      {renderPointBanner()}
      <Card className="rounded-2xl shadow-sm [&_.ant-card-body]:p-0">
        <div className="flex items-center justify-between border-0 border-b border-solid border-[#ededed] px-6 py-5 mobile:px-4">
          <div className="text-left">
            <p className="m-0 text-lg font-bold text-black">即將來到的活動</p>
            <p className="m-0 text-sm text-SKF-G-002">Upcoming Events</p>
          </div>
          <Button
            type="link"
            className="p-0 text-SKF-G-003"
            onClick={() => setActiveSection("history")}
          >
            全部消費紀錄 <RightOutlined />
          </Button>
        </div>
        <div className="p-6 mobile:p-4">
          {upcomingOrders.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 mobile:py-8">
              <div className="mb-4 flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#f3f3f3]">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.5 6.5C4.39543 6.5 3.5 7.39543 3.5 8.5V11C4.60457 11 5.5 11.8954 5.5 13C5.5 14.1046 4.60457 15 3.5 15V17.5C3.5 18.6046 4.39543 19.5 5.5 19.5H18.5C19.6046 19.5 20.5 18.6046 20.5 17.5V15C19.3954 15 18.5 14.1046 18.5 13C18.5 11.8954 19.3954 11 20.5 11V8.5C20.5 7.39543 19.6046 6.5 18.5 6.5H5.5Z"
                    stroke="#b0b0b0"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.5 13H14.5"
                    stroke="#b0b0b0"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p className="m-0 mb-4 text-[13px] font-medium text-[#9d9d9d]">
                目前沒有即將到來的活動
              </p>
              <Button
                type="primary"
                className="h-10 rounded-lg bg-black px-6 font-bold text-white border-0 hover:!bg-black/80 hover:!text-white"
                onClick={() => navigate("/home")}
              >
                前往購票
              </Button>
            </div>
          )}
          {upcomingOrders.map((order) => (
            <div key={order.id} className="text-left">
              <div className="mb-4 flex items-start justify-between gap-8">
                <div>
                  <p className="m-0 font-bold text-black">{order.title}</p>
                  <p className="m-0 mt-1 text-sm text-SKF-G-002">
                    {convertTimeStamp(getOrderDate(order))}
                  </p>
                </div>
                <Tag
                  color={getStatusTone(order.status)}
                  className="mobile:mt-3 font-semibold text-md py-0 px-2"
                >
                  {getOrderStatusLabel(order.status)}
                </Tag>
              </div>
              <div className="space-y-2">
                {order.details.slice(0, 2).map((detail) => (
                  <div
                    key={detail.id}
                    className="flex items-center justify-between rounded-xl bg-[#f7f7f7] px-3 py-3 gap-4"
                  >
                    <div>
                      <p className="m-0 font-medium text-black">
                        {detail.title}
                      </p>
                      <p className="m-0 text-xs text-SKF-G-002">#{detail.id}</p>
                    </div>
                    <div className="flex items-center">
                      <QrcodeOutlined className="p-0 font-bold text-SKF-Blue-001 mobile:mt-2 text-xs" />
                      <Button
                        type="link"
                        className="ml-[2px] p-0 font-bold text-SKF-Blue-001 mobile:mt-2 text-sm"
                        onClick={() => fetchDetail(detail)}
                      >
                        查看票券
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  const pointColumns = [
    {
      title: "日期",
      dataIndex: "creationDate",
      key: "creationDate",
      render: (text, record) => (
        <span className="text-SKF-G-003">
          {convertTimeStamp(text || record.date, "YYYY/MM/DD")}
        </span>
      ),
    },
    {
      title: "狀態",
      dataIndex: "type",
      key: "type",
      render: (text, record) => (
        <span className="text-black">
          {getWalletTransactionLabel(text, record.amount)}
        </span>
      ),
    },
    {
      title: "異動點數",
      dataIndex: "amount",
      key: "amount",
      render: (text, record) => {
        const absAmount = Math.abs(Number(text || 0));
        let sign = "";
        if (record.type === "Spend" || record.type === 2) {
          sign = "-";
        } else if (
          record.type === "TopUp" ||
          record.type === "Refund" ||
          record.type === 1 ||
          record.type === 3
        ) {
          sign = "+";
        } else {
          // 凍結、到期、更正等類型依後端給的正負號
          const amount = Number(text || 0);
          sign = amount > 0 ? "+" : amount < 0 ? "-" : "";
        }

        return (
          <span className="font-bold text-black">
            {sign}
            {absAmount.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: "交易後餘額",
      dataIndex: "availableBalance",
      key: "availableBalance",
      render: (text) => (
        <span className="text-black">
          {text != null ? `${Number(text).toLocaleString()} pts` : "-"}
        </span>
      ),
    },
    {
      title: "紀錄單號",
      dataIndex: "sourceId",
      key: "sourceId",
      render: (text, record) => (
        <span className="text-black">
          {text && hasOrderDetails(record.type) ? (
            <Button
              type="link"
              className="pl-0"
              onClick={() => handleNavigateToOrder(text)}
            >
              {text}
            </Button>
          ) : (
            text || "-"
          )}
        </span>
      ),
    },
  ];

  const renderPoints = () => (
    <div className="mx-auto max-w-[1020px] space-y-8">
      {renderPointBanner()}
      <Card className="rounded-2xl shadow-sm [&_.ant-card-body]:p-0">
        <div className="flex flex-col md:flex-row flex-wrap items-start md:items-center gap-4 border-0 border-b border-solid border-[#ededed] px-6 py-4">
          <DatePicker.RangePicker
            className="w-full md:w-auto rounded-lg"
            onChange={(dates, dateStrings) => {
              setSearchDates(dateStrings);
              if (!dates) {
                setPointQuery((prev) => ({
                  ...prev,
                  offset: 0,
                  startDateTime: undefined,
                  endDateTime: undefined,
                }));
              }
            }}
          />
          <div className="flex flex-row gap-2 md:gap-4 w-full md:w-auto">
            <Select
              placeholder="狀態"
              allowClear
              className="flex-1 md:flex-none md:w-32 rounded-lg"
              onChange={(val) => {
                setSearchType(val);
                if (val === undefined || val === null) {
                  setPointQuery((prev) => ({
                    ...prev,
                    offset: 0,
                    type: undefined,
                  }));
                }
              }}
              options={[
                { value: 1, label: "儲值" },
                { value: 2, label: "消費" },
                { value: 3, label: "退款" },
                { value: 6, label: "調整" },
              ]}
            />
            <Input
              placeholder="訂單編號"
              allowClear
              className="flex-1 md:flex-none md:w-40 rounded-lg"
              value={searchOrderId}
              onChange={(e) => {
                setSearchOrderId(e.target.value);
                if (!e.target.value) {
                  setPointQuery((prev) => ({
                    ...prev,
                    offset: 0,
                    sourceId: undefined,
                  }));
                }
              }}
            />
            <Button
              type="primary"
              className="rounded-lg bg-black px-3 md:px-6 font-bold text-white border-0 hover:!bg-black/80 hover:!text-white"
              onClick={() => {
                setPointQuery((prev) => ({
                  ...prev,
                  offset: 0,
                  type: searchType,
                  startDateTime: searchDates?.[0]
                    ? `${searchDates[0]}T00:00:00`
                    : undefined,
                  endDateTime: searchDates?.[1]
                    ? `${searchDates[1]}T23:59:59`
                    : undefined,
                  sourceId: searchOrderId || undefined,
                }));
              }}
            >
              搜尋
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table
            className="w-full min-w-[640px] [&_.ant-table-thead>tr>th]:bg-[#f7f7f7] [&_.ant-table-thead>tr>th]:text-SKF-G-002 [&_.ant-table-thead>tr>th]:font-medium [&_.ant-table-thead>tr>th]:px-4 [&_.ant-table-thead>tr>th]:py-3 [&_.ant-table-tbody>tr>td]:px-4 [&_.ant-table-tbody>tr>td]:py-4"
            rowKey={(record, index) => record.id || index}
            columns={pointColumns}
            dataSource={transactions}
            pagination={{
              current: Math.floor(pointQuery.offset / pointQuery.limit) + 1,
              pageSize: pointQuery.limit,
              total: pointTotal,
              onChange: (page, pageSize) => {
                setPointQuery((prev) => ({
                  ...prev,
                  limit: pageSize,
                  offset: (page - 1) * pageSize,
                }));
              },
            }}
            loading={isTransactionsLoading}
          />
        </div>
      </Card>
    </div>
  );

  const renderOrderCard = (order) => {
    const paymentFields = [
      {
        label: "點數使用",
        value: formatWalletDiscountAmount(order),
        strong: true,
      },
      { label: "實際付款", value: formatOrderAmount(order?.paidAmount) },
      { label: "支付手續費", value: formatOrderAmount(order?.chargeAmount) },
      {
        label: "退款至點數",
        value: formatOrderAmount(order?.refundToWalletAmount),
        strong: true,
      },
    ];

    return (
      <Card
        key={order.id}
        loading={isCardLoading}
        className="rounded-2xl shadow-sm [&_.ant-card-body]:p-0"
      >
        <div className="flex items-center gap-4 border-0 border-b border-solid border-[#ededed] px-5 py-5 mobile:items-start mobile:gap-3 mobile:px-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f1f1f1] text-SKF-G-002">
            <ProfileOutlined />
          </div>
          <div className="min-w-0 flex-1 text-left">
            <p className="m-0 truncate text-base font-bold text-black">
              {order.title}
            </p>
            <Tag color={getStatusTone(order.status)} className="mt-2">
              {getOrderStatusLabel(order.status)}
            </Tag>
            {order?.status === 64 && (
              <Tooltip title="超過一小時未完成付款，自動取消" color="#9D9D9D">
                <QuestionCircleFilled className="ml-1 text-SKF-G-002" />
              </Tooltip>
            )}
          </div>
          <div className="shrink-0 text-right">
            <p className="m-0 text-base font-bold text-black">
              NT${Number(order?.price || 0).toLocaleString()}
            </p>
            <p className="m-0 text-sm text-SKF-G-002">
              {order?.details?.length || 0} 張
            </p>
          </div>
          <Button
            type="text"
            className="shrink-0 bg-[#f1f1f1]"
            icon={
              order.isActiveCollapse ? (
                <CaretUpOutlined />
              ) : (
                <CaretDownOutlined />
              )
            }
            onClick={() => toggleOrder(order.id)}
          />
        </div>
        <div className="grid grid-cols-2 gap-x-10 gap-y-6 px-5 py-5 text-left tablet:grid-cols-1 mobile:grid-cols-2 mobile:gap-x-5 mobile:gap-y-5 mobile:px-4">
          <OrderInfoItem label="訂單編號" value={order?.id} />
          <OrderInfoItem
            label="購票時間"
            value={
              order?.creationDate ? convertTimeStamp(order.creationDate) : "-"
            }
          />
          <OrderInfoItem
            label="付款方式"
            value={paymentMethodMap[order?.paymentMethod]?.name}
          />
          <div className="grid min-w-0 grid-cols-4 gap-4 mobile:contents">
            {paymentFields.map((field) => (
              <OrderInfoItem
                key={field.label}
                label={field.label}
                value={field.value}
                strong={field.strong}
              />
            ))}
          </div>
        </div>
        {order.isActiveCollapse && (
          <div className="border-0 border-t border-solid border-[#ededed]">
            <Table
              className="w-full [&_.ant-table-thead>tr>th]:bg-[#f7f7f7]"
              rowKey="id"
              size="small"
              columns={ticketColumns}
              dataSource={order?.details}
              pagination={false}
              scroll={isMobile ? undefined : { x: undefined }}
              expandable={
                isMobile
                  ? {
                      expandedRowRender: (record) => (
                        <div className="py-2 space-y-1 text-sm text-left">
                          <p className="m-0">
                            <span className="text-SKF-G-002">票券名稱：</span>
                            {record.title}
                          </p>
                          <p className="m-0">
                            <span className="text-SKF-G-002">價格：</span>
                            {record.price}
                          </p>
                          <p className="m-0">
                            <span className="text-SKF-G-002">參加者：</span>
                            {record.name}
                          </p>
                          <p className="m-0">
                            <span className="text-SKF-G-002">Email：</span>
                            {record.email}
                          </p>
                        </div>
                      ),
                      rowExpandable: () => true,
                    }
                  : undefined
              }
            />
          </div>
        )}
      </Card>
    );
  };

  const renderHistory = () => (
    <div className="mx-auto max-w-[1020px] text-left">
      <h3 className="m-0 text-2xl font-bold text-black">消費紀錄</h3>
      <p className="m-0 mt-1 text-SKF-G-002">Purchase history</p>
      <p className="mb-8 mt-3 text-SKF-Blue-001">
        <a href="mailto:support@rftexp.com">
          如需退票請聯繫我們：support@rftexp.com
        </a>
      </p>
      <div className="mb-8 grid grid-cols-6 gap-1 rounded-2xl bg-[#f1f1f1] p-1 mobile:grid-cols-3">
        {[
          { value: 1, label: "訂單未付款" },
          { value: 2, label: "訂單待確認" },
          { value: 4, label: "訂單已付款" },
          { value: 8, label: "整筆訂單退款" },
          { value: 16, label: "部分訂單退款" },
          { value: 32, label: "建立失敗" },
        ].map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setHistoryTab(tab.value)}
            className={`rounded-xl border-0 px-3 py-3 font-bold cursor-pointer text-center text-sm mobile:text-xs mobile:px-2 ${
              historyTab === tab.value
                ? "bg-white text-black shadow"
                : "bg-transparent text-SKF-G-003"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {visibleOrders.length === 0 && (
          <Card className="rounded-2xl text-center text-SKF-G-002">
            目前沒有符合條件的消費紀錄
          </Card>
        )}
        {visibleOrders.map(renderOrderCard)}
      </div>
    </div>
  );

  const renderMain = () => {
    if (activeSection === "points") return renderPoints();
    if (activeSection === "history") return renderHistory();
    return renderInfo();
  };

  return (
    <div className="min-h-screen w-full bg-[#f8f8f8] text-black">
      <Spin spinning={isLoading} size="large" fullscreen />
      <div className="flex mobile:block">
        {renderSidebar()}
        <main className="min-w-0 flex-1 px-12 py-12 mobile:px-4 mobile:py-6 tablet:px-8">
          {renderMain()}
        </main>
      </div>
      <DetailModal
        isModalOpen={isModalOpen}
        handleCancel={handleCancel}
        record={record}
      />
      <ExchangeTicketModal
        isModalOpen={isExchangeTicketModalOpen}
        handleCancel={handleExchangeTicketCancel}
        handleExchangeTicketOk={handleExchangeTicketOk}
        record={exchangeTicketRecord}
      />
      <Modal
        title={<span className="text-xl font-bold">訂單詳情</span>}
        open={isOrderModalOpen}
        onCancel={() => setIsOrderModalOpen(false)}
        footer={null}
        centered
        width={520}
        className="[&_.ant-modal-content]:rounded-lg [&_.ant-modal-content]:border [&_.ant-modal-content]:border-solid [&_.ant-modal-content]:border-SKF-Blue-001"
      >
        {orderRecord && (
          <div className="border-0 border-t border-solid border-[#ededed] pt-4 text-left">
            <DetailRow label="訂單編號">{orderRecord.id}</DetailRow>
            <DetailRow label="活動名稱">{orderRecord.title}</DetailRow>
            <DetailRow label="購票時間">
              {convertTimeStamp(orderRecord.creationDate)}
            </DetailRow>
            <DetailRow label="狀態">
              {getOrderStatusLabel(orderRecord.status)}
            </DetailRow>
            <DetailRow label="價格">
              NT${Number(orderRecord.price || 0).toLocaleString()}
            </DetailRow>
            <DetailRow label="點數使用" strong>
              {formatWalletDiscountAmount(orderRecord)}
            </DetailRow>
            <DetailRow label="實際付款">
              {formatOrderAmount(orderRecord.paidAmount)}
            </DetailRow>
            <DetailRow label="支付手續費">
              {formatOrderAmount(orderRecord.chargeAmount)}
            </DetailRow>
            <DetailRow label="退款至點數" strong>
              {formatOrderAmount(orderRecord.refundToWalletAmount)}
            </DetailRow>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Member;
